import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Divider, Avatar, 
  Button, TextField, CircularProgress, Chip,
  Card, CardContent, useTheme, alpha,
  Alert, Snackbar, Fade, Slide, Grow,
  IconButton, Tooltip, Zoom, Badge, Popover
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Campaign as CampaignIcon,
  Email as EmailIcon,
  Send as SendIcon,
  EmojiEmotions as EmojiIcon,
  Delete as DeleteIcon,
  MarkEmailRead as MarkEmailReadIcon, 
  AccessTime as TimeIcon,
  ReplyAll as ReplyAllIcon,
  PersonOutline as PersonIcon
} from '@mui/icons-material';
import { formatDistance, format } from 'date-fns';
import { motion } from 'framer-motion';
import EmojiPicker from 'emoji-picker-react';
import messageService from '../../../services/messageService';
import { useNotification } from '../../../contexts/NotificationContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useMessages } from '../../../contexts/MessageContext';

const MemberMessageDetailPage = () => {
  const { messageId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const notification = useNotification();
  const { user } = useAuth();
  const { markMessageAsRead } = useMessages();
  
  // Animation states
  const [pageLoaded, setPageLoaded] = useState(false);
  const [showMessageContent, setShowMessageContent] = useState(false);
  const [showReplies, setShowReplies] = useState(false);
  const [showReplyForm, setShowReplyForm] = useState(false);
  
  // Add fallback notification system
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  
  // Emoji picker state
  const [emojiPickerAnchorEl, setEmojiPickerAnchorEl] = useState(null);
  const openEmojiPicker = Boolean(emojiPickerAnchorEl);
  
  // Handle emoji picker
  const handleEmojiPickerOpen = (event) => {
    setEmojiPickerAnchorEl(event.currentTarget);
  };
  
  const handleEmojiPickerClose = () => {
    setEmojiPickerAnchorEl(null);
  };
  
  const onEmojiClick = (emojiObject) => {
    setReplyText((prevText) => prevText + emojiObject.emoji);
    if (replyError) {
      setReplyError('');
    }
  };
  
  // Start animations when page loads
  useEffect(() => {
    const timer1 = setTimeout(() => {
      setPageLoaded(true);
    }, 100);
    
    const timer2 = setTimeout(() => {
      setShowMessageContent(true);
    }, 500);

    const timer3 = setTimeout(() => {
      setShowReplies(true);
    }, 900);
    
    const timer4 = setTimeout(() => {
      setShowReplyForm(true);
    }, 1300);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
    };
  }, []);
  
  // Create a reliable notification function
  const showLocalNotification = (message, severity = 'info') => {
    if (notification) {
      if (typeof notification.notify === 'function') {
        notification.notify(message, severity);
        return;
      } else if (typeof notification.showNotification === 'function') {
        notification.showNotification(message, severity);
        return;
      } else if (typeof notification.setNotification === 'function') {
        notification.setNotification({ message, severity, open: true });
        return;
      }
    }
    
    // Fallback to local snackbar
    setSnackbar({
      open: true,
      message,
      severity
    });
  };
  
  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };
  
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [replyError, setReplyError] = useState('');

  // Function to handle text field changes
  const handleReplyChange = (e) => {
    setReplyText(e.target.value);
    if (e.target.value.trim()) {
      setReplyError('');
    }
  };
  
  // Fetch message data
  useEffect(() => {
    const fetchMessage = async () => {
      setLoading(true);
      try {
        const response = await messageService.getMessageById(messageId);
        if (response.success) {
          setMessage(response.data);
          
          // Mark as read if not already read
          if (!response.data.isRead) {
            await markMessageAsRead(messageId);
          }
        } else {
          setError(response.error || 'Failed to fetch message');
          showLocalNotification('Failed to fetch message details', 'error');
        }
      } catch (err) {
        console.error('Error fetching message:', err);
        // Check for specific error status codes
        if (err.response && err.response.status === 403) {
          setError('You are not authorized to view this message.');
          showLocalNotification('You are not authorized to view this message', 'error');
        } else if (err.response && err.response.status === 404) {
          setError('Message not found. It may have been deleted.');
          showLocalNotification('Message not found', 'error');
        } else {
          setError('An error occurred while fetching the message');
          showLocalNotification('Error loading message', 'error');
        }
      } finally {
        setLoading(false);
      }
    };
    
    if (messageId) {
      fetchMessage();
    }
  }, [messageId]);
  
  // Function to send a reply
  const handleSendReply = async () => {
    if (!replyText.trim()) {
      setReplyError('Reply text is required');
      return;
    }
    
    setSendingReply(true);
    try {
      // Use the generic reply method instead of member-specific one
      const response = await messageService.replyToMessage(messageId, replyText);
      
      if (response.success) {
        showLocalNotification('Reply sent successfully', 'success');
        setMessage(response.data);
        setReplyText('');
      } else {
        showLocalNotification(response.error || 'Failed to send reply', 'error');
      }
    } catch (err) {
      console.error('Error sending reply:', err);
      showLocalNotification('An error occurred while sending your reply', 'error');
    } finally {
      setSendingReply(false);
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '70vh' 
      }}>
        <CircularProgress size={60} thickness={4} color="primary" />
        <Typography sx={{ mt: 2, color: 'text.secondary', fontWeight: 500 }}>
          Loading message...
        </Typography>
      </Box>
    );
  }
  
  if (error || !message) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate('/member/messages')} 
          sx={{ 
            mb: 3, 
            borderRadius: 2,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              transform: 'translateX(-5px)'
            }
          }}
        >
          Back to Messages
        </Button>
        
        <Zoom in={true}>
          <Paper 
            elevation={3}
            sx={{ 
              p: 4, 
              textAlign: 'center',
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.background.paper, 0.8),
              backdropFilter: 'blur(10px)',
              border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
              boxShadow: theme.shadows[3]
            }}
          >
            <Avatar 
              sx={{ 
                width: 70, 
                height: 70, 
                mx: 'auto', 
                mb: 3, 
                bgcolor: 'error.main',
                boxShadow: theme.shadows[3]
              }}
            >
              <EmailIcon sx={{ fontSize: 40 }} />
            </Avatar>
            
            <Typography variant="h5" color="error.main" sx={{ mb: 2, fontWeight: 'bold' }}>
              {error || 'Message not found'}
            </Typography>
            
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {error 
                ? "We couldn't retrieve this message. It may have been deleted or you might not have permission to view it."
                : "The message you're looking for could not be found."}
            </Typography>
            
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => navigate('/member/messages')}
              startIcon={<ArrowBackIcon />}
              sx={{ 
                mt: 2,
                px: 3,
                py: 1.2,
                borderRadius: 2,
                boxShadow: theme.shadows[3],
                background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  boxShadow: theme.shadows[5],
                  transform: 'translateY(-2px)'
                }
              }}
            >
              Return to Messages
            </Button>
          </Paper>
        </Zoom>
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Fade in={pageLoaded} timeout={500}>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate('/member/messages')} 
          sx={{ 
            mb: 3, 
            borderRadius: 2,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              transform: 'translateX(-5px)'
            }
          }}
        >
          Back to Messages
        </Button>
      </Fade>
      
      <Zoom in={pageLoaded} timeout={800}>
        <Paper 
          elevation={3}
          sx={{ 
            p: 0, 
            mb: 3, 
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: theme.shadows[3],
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          {/* Message Header */}
          <Box sx={{ 
            p: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.15)} 100%)`,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.7)}`
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <Badge
                    overlap="circular"
                    badgeContent={message.isRead ? null : "New"}
                    color="error"
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                  >
                    <Avatar sx={{ 
                      bgcolor: message.isBroadcast ? theme.palette.info.main : theme.palette.primary.main,
                      width: 56,
                      height: 56,
                      mr: 2,
                      boxShadow: theme.shadows[3]
                    }}>
                      {message.isBroadcast ? <CampaignIcon sx={{ fontSize: 28 }} /> : <EmailIcon sx={{ fontSize: 28 }} />}
                    </Avatar>
                  </Badge>
                </motion.div>
                
                <motion.div
                  initial={{ y: -20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    {message.subject}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    {message.isBroadcast && (
                      <Chip
                        label="Broadcast Message"
                        size="small"
                        color="info"
                        icon={<CampaignIcon />}
                        sx={{ 
                          borderRadius: 8,
                          '& .MuiChip-label': { px: 1 },
                          fontSize: '0.85rem'
                        }}
                      />
                    )}
                    
                    <Chip
                      icon={<TimeIcon fontSize="small" />}
                      label={format(new Date(message.createdAt), 'PPP p')}
                      size="small"
                      variant="outlined"
                      sx={{ 
                        borderRadius: 8,
                        bgcolor: alpha(theme.palette.background.paper, 0.7),
                        fontSize: '0.85rem'
                      }}
                    />
                    
                    {message.replies && message.replies.length > 0 && (
                      <Chip
                        icon={<ReplyAllIcon fontSize="small" />}
                        label={`${message.replies.length} ${message.replies.length === 1 ? 'reply' : 'replies'}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ borderRadius: 8 }}
                      />
                    )}
                  </Box>
                </motion.div>
              </Box>
              
              <Tooltip title={message.isRead ? "Message read" : "New message"}>
                <IconButton
                  sx={{
                    bgcolor: message.isRead ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.error.main, 0.1),
                    color: message.isRead ? theme.palette.success.main : theme.palette.error.main,
                    '&:hover': {
                      bgcolor: message.isRead ? alpha(theme.palette.success.main, 0.2) : alpha(theme.palette.error.main, 0.2),
                    }
                  }}
                >
                  <MarkEmailReadIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          
          <Fade in={showMessageContent} timeout={800}>
            <Box sx={{ p: 3 }}>
              {/* Message Content */}
              <Box sx={{ mb: 4 }}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  <Paper 
                    elevation={0}
                    sx={{ 
                      p: 3, 
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.background.paper, 0.5),
                      border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                      boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.05)'
                    }}
                  >
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        whiteSpace: 'pre-wrap',
                        lineHeight: 1.8,
                        fontSize: '1.05rem',
                        letterSpacing: '0.015em'
                      }}
                    >
                      {message.text}
                    </Typography>
                  </Paper>
                </motion.div>
              </Box>
              
              {/* From Information */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                >
                  <Paper sx={{ 
                    p: 2, 
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <Avatar 
                      sx={{ 
                        bgcolor: alpha(theme.palette.primary.main, 0.9),
                        mr: 1.5,
                        boxShadow: theme.shadows[1]
                      }}
                    >
                      <PersonIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        <strong>From Admin:</strong> {message.senderId?.firstName || ''} {message.senderId?.lastName || ''}
                      </Typography>
                      {message.senderId?.email && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>Email:</strong> {message.senderId.email}
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                </motion.div>
                
                {message.isBroadcast && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                  >
                    <Paper sx={{ 
                      p: 2, 
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.info.main, 0.08),
                      border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <CampaignIcon sx={{ mr: 1, color: theme.palette.info.main }} />
                      <Typography variant="body2" color="text.secondary">
                        <strong>Broadcast Message:</strong> Sent to All Members
                      </Typography>
                    </Paper>
                  </motion.div>
                )}
              </Box>
              
              <Divider sx={{ 
                my: 4,
                '&::before, &::after': {
                  borderColor: alpha(theme.palette.primary.main, 0.2),
                }
              }}>
                <Chip 
                  label="Conversation" 
                  color="primary" 
                  variant="outlined"
                  sx={{ fontSize: '0.9rem', fontWeight: 500 }}
                />
              </Divider>
              
              {/* Replies */}
              <Fade in={showReplies} timeout={1000}>
                <Box>
                  {message.replies && message.replies.length > 0 ? (
                    <Box sx={{ mb: 4 }}>
                      {message.replies.map((reply, index) => (
                        <Grow 
                          key={reply._id || index} 
                          in={showReplies} 
                          timeout={800} 
                          style={{ transformOrigin: reply.sender === 'admin' ? '0 0 0' : '100% 0 0', transitionDelay: `${200 + index * 150}ms` }}
                        >
                          <Card 
                            sx={{ 
                              mb: 2,
                              borderRadius: 2,
                              borderLeft: `4px solid ${reply.sender === 'admin' ? theme.palette.primary.main : theme.palette.success.main}`,
                              ml: reply.sender === 'admin' ? 0 : { xs: 0, md: 4 },
                              mr: reply.sender === 'admin' ? { xs: 0, md: 4 } : 0,
                              boxShadow: theme.shadows[2],
                              position: 'relative',
                              overflow: 'visible',
                              '&:hover': {
                                boxShadow: theme.shadows[4],
                              },
                              transition: 'all 0.2s ease-in-out',
                            }}
                          >
                            {/* Reply sender avatar */}
                            <Avatar 
                              sx={{
                                position: 'absolute',
                                left: reply.sender === 'admin' ? -16 : 'auto',
                                right: reply.sender === 'admin' ? 'auto' : -16,
                                top: -16,
                                bgcolor: reply.sender === 'admin' ? theme.palette.primary.main : theme.palette.success.main,
                                border: `2px solid ${theme.palette.background.paper}`,
                                boxShadow: theme.shadows[2]
                              }}
                            >
                              {reply.sender === 'admin' ? <PersonIcon /> : <ReplyAllIcon />}
                            </Avatar>
                            
                            <CardContent sx={{ p: 3, pt: 2.5 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography 
                                  variant="subtitle2" 
                                  sx={{
                                    color: reply.sender === 'admin' ? theme.palette.primary.dark : theme.palette.success.dark,
                                    fontWeight: 'bold'
                                  }}
                                >
                                  {reply.sender === 'admin' ? 'Admin' : 'You'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {formatDistance(new Date(reply.createdAt), new Date(), { addSuffix: true })}
                                </Typography>
                              </Box>
                              <Typography 
                                variant="body1" 
                                sx={{ 
                                  whiteSpace: 'pre-wrap',
                                  lineHeight: 1.6,
                                  letterSpacing: '0.01em'
                                }}
                              >
                                {reply.text}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grow>
                      ))}
                    </Box>
                  ) : (
                    <Box sx={{ mb: 4, textAlign: 'center' }}>
                      <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        No replies yet
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Fade>
              
              {/* Reply Form */}
              <Grow in={showReplyForm} timeout={1000}>
                <Box sx={{ 
                  pt: 2,
                  px: 3,
                  pb: 3,
                  mt: 2,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.background.paper, 0.5),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <ReplyAllIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                    <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                      Reply to this message
                    </Typography>
                  </Box>
                  
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Type your reply here..."
                    value={replyText}
                    onChange={handleReplyChange}
                    error={!!replyError}
                    helperText={replyError}
                    disabled={sendingReply}
                    sx={{ 
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.background.paper, 0.7),
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          bgcolor: alpha(theme.palette.background.paper, 0.9),
                          boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.1)}`
                        },
                        '&.Mui-focused': {
                          boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`
                        }
                      }
                    }}
                  />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Tooltip title="Add emoji">
                      <IconButton 
                        onClick={handleEmojiPickerOpen}
                        disabled={sendingReply}
                        color="primary"
                        sx={{ 
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.2),
                          }
                        }}
                      >
                        <EmojiIcon />
                      </IconButton>
                    </Tooltip>
                    
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={sendingReply ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                      onClick={handleSendReply}
                      disabled={sendingReply || !replyText.trim()}
                      sx={{ 
                        borderRadius: 8,
                        px: 3,
                        py: 1,
                        background: `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        boxShadow: theme.shadows[2],
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          boxShadow: theme.shadows[4],
                          transform: 'translateY(-2px)'
                        }
                      }}
                    >
                      {sendingReply ? 'Sending...' : 'Send Reply'}
                    </Button>
                  </Box>
                </Box>
              </Grow>
            </Box>
          </Fade>
        </Paper>
      </Zoom>
      
      {/* Emoji Picker Popover */}
      <Popover
        open={openEmojiPicker}
        anchorEl={emojiPickerAnchorEl}
        onClose={handleEmojiPickerClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: theme.shadows[5],
            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`
          }
        }}
      >
        <EmojiPicker 
          onEmojiClick={onEmojiClick} 
          lazyLoadEmojis={true}
        />
      </Popover>
      
      {/* Fallback notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          variant="filled"
          sx={{ 
            width: '100%',
            borderRadius: 2,
            boxShadow: theme.shadows[5]
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MemberMessageDetailPage;